import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import {
  DimensionEvent,
  DimensionEventStatus,
  ScheduledAssessment,
  ScheduledAssessmentType,
} from "@dimensional-engineering/dimensional-models";
import { Roboto_Mono } from "next/font/google";
import { Timestamp } from "firebase/firestore";

import styles from "./ContinueAssessmentsBar.module.css";

import elementsJsonData from "../../../assets/traitInfo/elements.json";

import {
  DimensionDataType,
  dimensionData as dimensionInitialData,
  dimensionCompletedTimes,
  dimensionData,
  shortFormToLongFormDimensionSlugMap,
  DimensionButtonEventStatus,
} from "@/assets/traitInfo/dimensionData";
import { RootState } from "@/redux/store";
import ForwardArrow from "@/components/shared/buttons/ForwardArrow";
import {
  generateDimensionButtonStatus,
  hasUserCompletedCorePlusShortForms,
  listUserCompletedDimensionsNonGating,
} from "@/components/authenticatedProfile/tabs/AuthCompareTab/PeerStories/utils";
import { ExtendedScale } from "@/models/sharedModels";
import DimensionCard, {
  ElementDimensionCard,
  PatternDimensionCard,
} from "./DimensionCard";

const font = Roboto_Mono({ subsets: ["latin"] });

export type AssessmentBarType = {
  type: "dimension" | "systemDimension" | "traitElement" | "traitPattern";
  slug: string;
  lastOccurrence?: Timestamp | null | undefined;
  status: DimensionButtonEventStatus;
  gated?: boolean;
};

export default function ContinueAssessmentsBar() {
  const router = useRouter();

  const dimensionEvents = useSelector(
    (state: RootState) => state.user.dimensionEvents
  );

  const { displayData, assessmentTypeDataDimensions } = useGetDimensionStatus();
  const systemInitiatedSlugs = useGetSystemInitiatedDimensions();
  const { assessmentTypeDataTraits } = useGetTraitAssessments();

  const notification = localStorage.getItem("viewScheduledAssessment");

  const mapped = [assessmentTypeDataDimensions, assessmentTypeDataTraits]
    .flat()
    .filter((a) => {
      if (a.type === "dimension" || a.type === "systemDimension") {
        const progress: number =
          dimensionEvents?.find((d) => d.dimensionSlug === a.slug)?.progress ??
          0;
        const dim = displayData.find(
          (d) => d.slug === a.slug || d.longFormSlug === a.slug
        ) as DimensionDataType;
        if (dim.isPremium) {
          return false;
        }
        if (a.status === "Continue" && progress === 1) {
          const longformDimension = dimensionEvents?.find(
            (d) => d.dimensionSlug === dim.longFormSlug
          );
          if (!longformDimension) {
            return false;
          } else {
            if (longformDimension.event === DimensionEventStatus.complete) {
              return false;
            }
          }
        }
      }
      return true;
    })
    .sort((a, b) => {
      const priority: Record<
        "dimension" | "systemDimension" | "traitElement" | "traitPattern",
        number
      > = {
        systemDimension: 0,
        traitElement: 0,
        traitPattern: 0,
        dimension: 1,
      };
      const aType = a.type;
      const bType = b.type;

      const comparison = priority[aType] - priority[bType];

      if (comparison !== 0) {
        return comparison;
      }

      const aLast = a.lastOccurrence;
      const bLast = b.lastOccurrence;

      if (aLast && !bLast) return -1;
      if (!aLast && bLast) return 1;
      if (aLast && bLast) return bLast.toMillis() - aLast.toMillis();

      const statusA = a.status;
      const statusB = b.status;

      const statusPriority: Record<DimensionButtonEventStatus, number> = {
        Continue: 0,
        Start: 1,
        View: 2,
      };

      const statusComparison =
        statusPriority[statusA] - statusPriority[statusB];

      if (statusComparison !== 0) {
        return statusComparison;
      }

      const orderA =
        displayData.find((d) => d.slug === a.slug || d.longFormSlug === a.slug)
          ?.order ?? Infinity;
      const orderB =
        displayData.find((d) => d.slug === b.slug || d.longFormSlug === b.slug)
          ?.order ?? Infinity;

      return orderA - orderB;
    })
    .map((assessment) => {
      if (
        assessment.type === "dimension" ||
        assessment.type === "systemDimension"
      ) {
        const dim = displayData.find(
          (d) =>
            d.slug === assessment.slug || d.longFormSlug === assessment.slug
        ) as DimensionDataType;
        let progress: number =
          dimensionEvents?.find((d) => d.dimensionSlug === dim.slug)
            ?.progress ?? 0;
        let estimatedCompletionTime: number =
          dimensionCompletedTimes[
            dim.slug as keyof typeof dimensionCompletedTimes
          ];

        const systemInitiated: boolean =
          systemInitiatedSlugs.includes(dim.slug) ||
          (dim.longFormSlug !== undefined &&
            systemInitiatedSlugs.includes(dim.longFormSlug));

        if (dim.longFormSlug) {
          const foundLongForm = dimensionEvents?.find(
            (d) => d.dimensionSlug === dim.longFormSlug
          );
          if (foundLongForm) {
            progress = foundLongForm.progress ?? 0;
            estimatedCompletionTime =
              dimensionCompletedTimes[
                foundLongForm.dimensionSlug as keyof typeof dimensionCompletedTimes
              ];
          } else {
            if (systemInitiated) {
              estimatedCompletionTime =
                dimensionCompletedTimes[
                  dim.longFormSlug as keyof typeof dimensionCompletedTimes
                ];
            }
          }
        }

        if (systemInitiated) {
          progress = 0;
        }

        return (
          <DimensionCard
            dimension={dim}
            systemInitiated={systemInitiated}
            estimatedCompletionTime={estimatedCompletionTime}
            progress={progress}
            key={assessment.slug}
          />
        );
      }

      if (assessment.type === "traitElement") {
        return (
          <ElementDimensionCard
            status={assessment.status}
            key={assessment.slug}
            slug={assessment.slug}
            gated={assessment.gated ? true : false}
          />
        );
      }

      if (assessment.type === "traitPattern") {
        return (
          <PatternDimensionCard
            status={assessment.status}
            key={assessment.slug}
            slug={assessment.slug}
            gated={assessment.gated ? true : false}
          />
        );
      }
    });

  useEffect(() => {
    if (notification) {
      const data = JSON.parse(notification);
      const assessmentSlug = data.assessmentSlug;

      const element = document.getElementById(assessmentSlug);
      if (element && mapped.length) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        localStorage.removeItem("viewScheduledAssessment");
      }
    }
  }, [mapped, notification]);

  if (!mapped.length) {
    return <div style={{ marginTop: "-20px" }}></div>;
  }

  return (
    <section className={styles.main}>
      <h2 className={styles.heading}>Continue your journey</h2>
      <div id="continueWrapper" className={styles.wrapper}>
        {mapped}
      </div>
      <div
        onClick={() => router.push("/app/assessment")}
        className={styles.viewAllDiv}
      >
        <p className={`${styles.viewAllText} ${font.className}`}>
          View all Dimensions
        </p>
        <ForwardArrow />
      </div>
    </section>
  );
}

function useGetDimensionStatus() {
  const dimensionEvents = useSelector(
    (state: RootState) => state.user.dimensionEvents
  );
  const scheduledAssessments = useSelector(
    (state: RootState) => state.scheduledAssessments.scheduledAssessments
  );

  const [displayData, setDimensionData] = useState<DimensionDataType[]>([]);
  const [assessmentTypeDataDimensions, setAssessmentTypeData] = useState<
    AssessmentBarType[]
  >([]);

  const systemInitiatedSlugs = useGetSystemInitiatedDimensions();

  useEffect(() => {
    let data: DimensionDataType[] = JSON.parse(
      JSON.stringify(dimensionInitialData)
    );
    const finishedDimensions =
      listUserCompletedDimensionsNonGating(dimensionEvents);
    for (let i = 0; i < data.length; i++) {
      const dimension = data[i];
      const eventStatus = generateDimensionButtonStatus(
        dimension,
        dimensionEvents
      );
      dimension.event = eventStatus;
      if (eventStatus === "View") {
        //the dimension is finished
        //We only make it a start if the system initiated requires it
        if (
          systemInitiatedSlugs.includes(dimension.slug) ||
          (dimension.longFormSlug &&
            systemInitiatedSlugs.includes(dimension.longFormSlug))
        ) {
          dimension.event = "Start";
        }
      }
    }
    setDimensionData(data);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionEvents, systemInitiatedSlugs]);

  useEffect(() => {
    const tempAssessmentTypeData: AssessmentBarType[] = [];

    displayData.forEach((d) => {
      const foundSystemAssessment = scheduledAssessments.find(
        (s) =>
          s.assessmentSlug === d.longFormSlug || s.assessmentSlug === d.slug
      );
      if (d.event !== "View") {
        tempAssessmentTypeData.push({
          type: systemInitiatedSlugs.includes(d.slug)
            ? "systemDimension"
            : "dimension",
          slug: systemInitiatedSlugs.includes(d.slug)
            ? d.longFormSlug ?? d.slug
            : d.slug,
          lastOccurrence: systemInitiatedSlugs.includes(d.slug)
            ? foundSystemAssessment?.lastOccurrence
              ? Timestamp.fromMillis(
                  foundSystemAssessment.lastOccurrence.toMillis()
                )
              : null
            : null,
          gated: false,
          status: d.event,
        });
      }
    });
    setAssessmentTypeData(tempAssessmentTypeData);
  }, [displayData]);

  return { displayData, assessmentTypeDataDimensions };
}

function useGetSystemInitiatedDimensions() {
  const [slugs, setSlugs] = useState<string[]>([]);

  const dimensionEvents = useSelector(
    (state: RootState) => state.user.dimensionEvents
  );

  const scheduledAssessments = useSelector(
    (state: RootState) => state.scheduledAssessments.scheduledAssessments
  );

  useEffect(() => {
    // System-tracked card will only show if:
    // 1. If it has a long-form:
    //     1. **ALL** short forms of dimensions that have long forms (the core+) are complete
    //     2. Long-form is not in progress
    // 2. If it does not have a long-form:
    //     1. short form is complete
    //Filter out the ScheduledAssessment IF systemInitiatedCompletionDate is within 30 days after ScheduledAssessment.lastOccurrence
    const canShowSlugs: string[] = [];

    const corePlusFinished =
      hasUserCompletedCorePlusShortForms(dimensionEvents);

    scheduledAssessments.forEach((assessment) => {
      if (assessment.assessmentType !== ScheduledAssessmentType.dimension) {
        return;
      }
      const hasLongForm = dimensionData.find(
        (d) => d.longFormSlug === assessment.assessmentSlug
      );
      if (hasLongForm) {
        //need to check for core+ completion here
        //need to make sure long form is not in progress
        const foundLongFormEvent = dimensionEvents?.find(
          (e) => e.dimensionSlug === hasLongForm.longFormSlug
        );
        const longFormInProgress =
          foundLongFormEvent !== undefined &&
          (foundLongFormEvent.event === DimensionEventStatus.started ||
            foundLongFormEvent.event === DimensionEventStatus.reset ||
            foundLongFormEvent.event === DimensionEventStatus.resetGated);

        if (
          corePlusFinished &&
          !longFormInProgress &&
          !isDimensionEventSystemInitiatedCompletionDateWithin30DaysOfCurrentScheduledAssessment(
            assessment,
            foundLongFormEvent
          )
        ) {
          canShowSlugs.push(assessment.assessmentSlug);
        }
      } else {
        //short form only
        //need to make sure short form is complete
        const foundShortFormEvent = dimensionEvents?.find(
          (e) => e.dimensionSlug === assessment.assessmentSlug
        );
        if (
          foundShortFormEvent &&
          foundShortFormEvent.event === DimensionEventStatus.complete &&
          !isDimensionEventSystemInitiatedCompletionDateWithin30DaysOfCurrentScheduledAssessment(
            assessment,
            foundShortFormEvent
          )
        ) {
          canShowSlugs.push(assessment.assessmentSlug);
        }
      }
    });

    setSlugs(canShowSlugs);
  }, [scheduledAssessments, dimensionEvents]);

  return slugs;
}

function useGetTraitAssessments() {
  const scheduledAssessments = useSelector(
    (state: RootState) => state.scheduledAssessments.scheduledAssessments
  );
  const traitAssessmentResults = useSelector(
    (state: RootState) => state.scheduledAssessments.traitAssessmentResults
  );
  const traitAssessmentInProgressScheduleIdentifiers = useSelector(
    (state: RootState) =>
      state.scheduledAssessments.traitAssessmentInProgressScheduleIdentifiers
  );

  const dimensionEvents = useSelector(
    (state: RootState) => state.user.dimensionEvents
  );

  const [traitsToShow, setAssessments] = useState<ScheduledAssessment[]>([]);
  const [gatedTraitSlugs, setGatedSlugs] = useState<string[]>([]);
  const [assessmentTypeDataTraits, setAssessmentTypeData] = useState<
    AssessmentBarType[]
  >([]);
  const [inProgressSlugs, setInProgressSlugs] = useState<string[]>([]);

  useEffect(() => {
    const corePlusFinished =
      hasUserCompletedCorePlusShortForms(dimensionEvents);

    const tempAssessments: ScheduledAssessment[] = [];
    const tempGatedSlugs: string[] = [];
    const tempInPogressSlugs: string[] = [];

    scheduledAssessments.forEach((a) => {
      const foundresult = traitAssessmentResults.find(
        (r) => r.scheduleIdentifier === a.scheduleIdentifier
      );
      if (foundresult) {
        return;
      }

      const foundInProgress = traitAssessmentInProgressScheduleIdentifiers.find(
        (i) => i === a.scheduleIdentifier
      );

      if (foundInProgress) {
        tempInPogressSlugs.push(a.assessmentSlug);
      }

      if (a.assessmentType === ScheduledAssessmentType.element) {
        const foundElement = elementsJsonData.find(
          (e) => e.slug === a.assessmentSlug
        ) as unknown as ExtendedScale;
        const dimensionSlug = foundElement.dimensionSlug;
        const longFormSlug = shortFormToLongFormDimensionSlugMap[dimensionSlug];

        const foundDimensionEventLong = dimensionEvents?.find(
          (d) => d.dimensionSlug === longFormSlug
        );

        if (!corePlusFinished) {
          //show it for gating
          tempAssessments.push(a);
          tempGatedSlugs.push(a.assessmentSlug);
        } else {
          if (
            foundDimensionEventLong?.event === DimensionEventStatus.complete
          ) {
            //ready to do assessment for trait
            tempAssessments.push(a);
          }
        }
      } else if (a.assessmentType === ScheduledAssessmentType.pattern) {
        if (corePlusFinished) {
          //ready to do assessment for trait
          tempAssessments.push(a);
        } else {
          tempAssessments.push(a);
          tempGatedSlugs.push(a.assessmentSlug);
        }
      }
    });
    setAssessments(tempAssessments);
    setGatedSlugs(tempGatedSlugs);
    setInProgressSlugs(tempInPogressSlugs);
  }, [
    scheduledAssessments,
    dimensionEvents,
    traitAssessmentInProgressScheduleIdentifiers,
    traitAssessmentResults,
  ]);

  useEffect(() => {
    const tempAssessmentTypeData: AssessmentBarType[] = [];
    traitsToShow.forEach((t) => {
      tempAssessmentTypeData.push({
        type:
          t.assessmentType === ScheduledAssessmentType.element
            ? "traitElement"
            : "traitPattern",
        slug: t.assessmentSlug,
        lastOccurrence: t.lastOccurrence
          ? Timestamp.fromMillis(t.lastOccurrence.toMillis())
          : undefined,
        gated: gatedTraitSlugs.includes(t.assessmentSlug),
        status: inProgressSlugs.includes(t.assessmentSlug)
          ? "Continue"
          : "Start",
      });
    });
    setAssessmentTypeData(tempAssessmentTypeData);
  }, [traitsToShow, gatedTraitSlugs, inProgressSlugs]);

  return { traitsToShow, gatedTraitSlugs, assessmentTypeDataTraits };
}

function isDimensionEventSystemInitiatedCompletionDateWithin30DaysOfCurrentScheduledAssessment(
  scheduledAssessment: ScheduledAssessment,
  dimensionEvent: DimensionEvent | null | undefined
) {
  const lastOccurence: FirebaseFirestore.Timestamp | null | undefined =
    scheduledAssessment.lastOccurrence;
  const systemInitiatedCompletionDate: number | null | undefined =
    dimensionEvent?.systemInitiatedCompletionDate;

  if (!lastOccurence || !systemInitiatedCompletionDate) {
    return false;
  }

  const lastOccurrenceMillis = lastOccurence.toMillis();
  const thirtyDaysInMillis = 30 * 24 * 60 * 60 * 1000;
  const windowStart = lastOccurrenceMillis;
  const windowEnd = lastOccurrenceMillis + thirtyDaysInMillis;

  const isWithinWindow =
    systemInitiatedCompletionDate >= windowStart &&
    systemInitiatedCompletionDate <= windowEnd;

  return isWithinWindow;
}
